/**
 * Main logic to determine if setup guide should be shown to the user.
 *
 * Setup guide is shown when ALL conditions are met:
 *
 * Prerequisites
 * 1. URL must contain connection type parameter (?p=w|c|s)
 * 2. Initial setup must NOT be completed in storage
 * 3. Connection type must be valid: wifi, cellular, or sidewalk
 * 4. Setup flow must be enabled in IAP config for device/connection type
 * 5. Connection type must be supported by the device type
 * 6. Device with QR codentify must NOT be already registered (if codentify provided)
 * 7. User must NOT have any registered devices (if no codentify OR codentify device not registered)
 *
 * If any skip condition is met, bypasses setup guide and redirects to home.
 */

import appConfig from '@/configs/appConfig';
import DEVICE_TYPES_DATA from '@/consts/device/deviceTypesData';

import {selectIsAnyDeviceRegistered, selectIsDeviceRegisteredByCodentify} from '../state/selectors/iotDevices';
import {selectInitialConnectionType, selectInitialDeviceType, selectQrCodentify} from '../state/selectors/session';
import {setInitialDeviceType, setShouldRedirectToHome, setShouldSetupGuideBeShown} from '../state/slice/session';
import {dispatch, getState} from '../state/store';
import connectionGuideService from './connectionGuideService';
import log from './logger/log';
import notificationService from './notificationService';

// Checks if setup flow is enabled for current connection and device type.
const getIsSetupFlowEnabled = () => {
    const connectionType = selectInitialConnectionType(getState());
    const deviceType = selectInitialDeviceType(getState());

    return appConfig.getIsSetupFlowEnabled(deviceType, connectionType);
};

// Checks if the selected connection type is supported by the selected device type
const getIsConnectionTypeSupportedByDeviceType = () => {
    const connectionType = selectInitialConnectionType(getState());
    let deviceType = selectInitialDeviceType(getState());

    if (!deviceType) {
        const defaultDeviceType = appConfig.getDefaultDeviceType();

        dispatch(setInitialDeviceType(defaultDeviceType));
        deviceType = defaultDeviceType;
    }

    const supportedConnectionTypes = DEVICE_TYPES_DATA[deviceType]?.connectionTypes;

    if (!supportedConnectionTypes) {
        return false;
    }

    const isSupported = supportedConnectionTypes.includes(connectionType);

    if (!isSupported) {
        log.warn(`setupGuideService: ${deviceType} does not support connection type: ${connectionType}`);
    }

    return isSupported;
};

const checkShouldSetupGuideBeShown = () => {
    const connectionType = selectInitialConnectionType(getState());

    if (!connectionType) {
        log.debug('setupGuideService: Connection type is not defined in initial parameters');
        skipSetupGuide();
        return;
    }

    const isSetupFlowEnabled = getIsSetupFlowEnabled();
    const deviceType = selectInitialDeviceType(getState());

    if (!isSetupFlowEnabled) {
        log.warn(`setupGuideService: Setup flow is disabled for ${connectionType} connection and ${deviceType} device`);
        skipSetupGuide();
        return;
    }

    // Skip if device doesn't support this connection type
    const isConnectionTypeSupported = getIsConnectionTypeSupportedByDeviceType();

    if (!isConnectionTypeSupported) {
        log.warn(`setupGuideService: ${deviceType} does not support ${connectionType} connection`);
        skipSetupGuide();
        return;
    }

    const isAnyDeviceRegistered = selectIsAnyDeviceRegistered(getState());

    // Skip if user has any registered devices
    if (isAnyDeviceRegistered) {
        const qrCodentify = selectQrCodentify(getState());

        if (qrCodentify) {
            const isDeviceRegistered = selectIsDeviceRegisteredByCodentify(qrCodentify)(getState());

            // For LTE/Sidewalk: show notification if device with this codentify is already registered
            if (isDeviceRegistered) {
                log.warn(`setupGuideService: Device with codentify ${qrCodentify} is already registered`);
                notificationService.showDeviceRegistrationSuccessNotification();
            }
        }

        log.warn(`setupGuideService: Setup guide skipped - user has registered devices`);
        skipSetupGuide({redirectToHome: true});
        return;
    }

    log.debug(`setupGuideService: Setup guide will be shown`);

    dispatch(setShouldSetupGuideBeShown(true));
};

const skipSetupGuide = ({redirectToHome} = {}) => {
    connectionGuideService.resetInitialSetupState();
    connectionGuideService.markSetupGuideAsShown();

    if (redirectToHome) {
        dispatch(setShouldRedirectToHome(true));
    }
};

export default {
    checkShouldSetupGuideBeShown,
};
